import { Timestamp } from 'firebase/firestore';
import { StaticImageData } from 'next/image';

// Website

export interface MenuItem {
  title: string;
  href: string | null;
  target?: string;
}

export type SubMenuItem = MenuItem & {
  subMenu?: SubMenuItem[];
};

export type MenuItemWithSubMenu = MenuItem & {
  subMenu?: SubMenuItem[];
};

export interface ImageProps {
  // `string` covers client-editable images served from the WP media library
  // (PAS-67); bundled chrome (icons, logos) still passes a static import.
  src: string | StaticImageData;
  alt: string;
  height?: number;
  width?: number;
  position?: 'left' | 'right';
  fill?: boolean;
  priority?: boolean;
}

// Compass

export enum MeetingType {
  AA = 'Alcoholics Anonymous',
  NA = 'Narcotics Anonymous',
  NAMI = 'National Alliance on Mental Illness (NAMI)',
  R = 'Refuge Recovery',
  Other = 'Other',
}

export interface MeetingResponse {
  id: string;
  geohash: string;
  coords: [number, number];
  address: string;
  day: Day;
  name: string;
  note: string;
  // Hybrid meetings' public join link (https only), absent otherwise.
  conferenceUrl?: string;
  time: string;
  type: keyof typeof MeetingType;
  // Provenance: chapter-site URLs this meeting was scraped from (PAS-25).
  // Legacy docs lack these fields; read paths default them.
  sources: string[];
  formats: string[];
  active?: boolean;
}

export interface ReportResponse {
  id: string;
  dateSubmitted: Timestamp;
  issue: MeetingIssue;
  meetingId: string;
  meetingName: string;
  note: string;
}

export interface TypesenseResponse {
  id: string;
  name: string;
  search?: boolean;
}

export type Day =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

export interface Meeting {
  display: boolean;
  id: string;
  name: string;
  address: string;
  note?: string;
  // Hybrid meetings' public join link (https only), absent otherwise.
  conferenceUrl?: string;
  day: Day;
  time: string;
  coords: google.maps.LatLngLiteral;
  type: MeetingType;
  formats?: string[];
  active?: boolean;
}

export interface Marker {
  coords: google.maps.LatLngLiteral;
  marker: google.maps.marker.AdvancedMarkerElement;
  markerId: string;
  meetings: Meeting[];
}

export interface MeetingWithMarkerID extends Meeting {
  markerId: string;
}

export enum MeetingIssue {
  DOES_NOT_EXIST = 'does_not_exist',
  TIME_INCORRECT = 'time_incorrect',
  LOCATION_INCORRECT = 'location_incorrect',
  TYPE_INCORRECT = 'type_incorrect',
  OTHER = 'other',
}

export interface MeetingReport {
  meetingId: string;
  meetingName: string;
  issue: keyof MeetingIssue;
  note: string;
}

export interface FiltersState {
  day: Day[];
  time: {
    compare: Compare;
    value: string;
  };
  type: MeetingType[];
}

export type Compare = null | '>=' | '<' | '=';

// Theme
export type BackgroundColorClasses = 'bg-blue' | 'bg-black' | 'bg-white';

export type TextColorClasses = 'text-black' | 'text-white' | 'text-charcoal';

// Typography
export type HeadingProps = {
  color?: TextColorClasses;
  level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  role?: string;
  roleLevel?: string;
  style?: string;
} & ({ html: true; text: string } | { html?: false; text: React.ReactNode });

// `html: true` is an injection sink, so it only accepts a string — the trusted
// WordPress markup path. Every other caller renders `text` as a React child,
// which React escapes, and may therefore pass nodes as well as strings.
export type ParagraphProps = {
  color?: TextColorClasses | 'text-red';
  style?: string;
  tag?: 'p' | 'span' | 'address' | 'div';
} & ({ html: true; text: string } | { html?: false; text: React.ReactNode });

// Admin
export interface AdminDashboardProps {
  type: DashboardDisplayType;
}

export type DashboardDisplayType =
  | 'none'
  | 'add'
  | 'upload'
  | 'edit'
  | 'reports'
  | 'scrape-review';

export interface MeetingAddress {
  address: string;
  coords: google.maps.LatLngLiteral;
}

export interface EditAddressState {
  address: string;
  coords: google.maps.LatLngLiteral;
}

export interface MeetingFromSpreadsheet {
  id: string;
  address: string;
  name: string;
  type: string;
  day: string;
  hour: string;
  minutes: string;
  note: string;
}

// Scraper contract (PAS-61)
//
// Faithful port of the meeting-scraper repo's Firestore schemas — kept in sync
// by hand, since the repos don't share code. Source (passenger-meeting-scraper):
//   src/schemas.ts   chapterSchema, scrapeReviewSchema, scrapeRunSchema
// If these disagree with the scraper's zod schemas, the scraper wins: it is the
// only writer of `chapters`, `scrapeReviews` and `scrapeRuns`.
//
// One deliberate divergence: the scraper's `timestamp` schema transforms
// Firestore Timestamps to `Date` on read, so its inferred types say `Date`.
// Here the fields are `Timestamp`, because that is what the client SDK actually
// returns to this app (matching `ReportResponse.dateSubmitted` above). Call
// `.toDate()` at the point of use. Every other field matches field-for-field,
// including optionality.

export type Fellowship = 'AA' | 'NA';

export type FeedType = 'tsml' | 'meetingguide' | 'bmlt' | 'html' | 'unknown';

export type LegacyReviewKind = 'add' | 'update' | 'inactive';

export type ReviewKind = LegacyReviewKind | 'orphan' | 'moved';

export type ReviewStatus = 'pending' | 'approved' | 'denied';

export type ExtractionMethod = 'feed' | 'intercept' | 'html' | 'llm';

export type TierStatus = 'ok' | 'unchanged' | 'failed' | 'skipped';

export type InactiveSkipReason =
  | 'scrape-failed'
  | 'unchanged-content'
  | 'zero-candidates'
  | 'low-confidence'
  | 'flagged-records'
  | 'llm-tier';

/** Registry of chapter sites the scraper crawls. */
export interface Chapter {
  id: string;
  name: string;
  fellowship: Fellowship;
  country: 'US' | 'UK';
  region: string;
  directoryUrl: string;
  meetingsUrl: string;
  feedType: FeedType;
  feedUrl?: string;
  /** null until the first successful scrape. */
  lastScrapedAt: Timestamp | null;
  lastContentHash: string | null;
  active: boolean;
  notes: string;
}

/**
 * A proposed change to the live `meetings` collection, awaiting review.
 *
 * `id` is NOT a stored field — the scraper strips it before writing and uses
 * `dedupeKey` as the document id, so read paths must reconstruct it from the
 * snapshot id.
 */
interface ScrapeReviewResolution {
  id: string;
  status: ReviewStatus;
  dedupeKey: string;
  createdAt: Timestamp;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
}

export interface LegacyScrapeReview extends ScrapeReviewResolution {
  chapterId: string;
  source: string;
  kind: LegacyReviewKind;
  /** MeetingResponse-shaped candidate. */
  proposed: Record<string, unknown>;
  existingMeetingId?: string;
  /**
   * Per-field change map, e.g. `{ time: { before: '1830', after: '1900' } }`.
   *
   * Both keys are optional, matching the scraper: its schema declares them with
   * `z.unknown()`, and because `unknown` admits `undefined` zod infers the keys
   * as optional — `{ before?: unknown; after?: unknown }`. Verified by parsing
   * `{ t: { before: 1 } }` and `{ t: {} }` against the real `scrapeReviewSchema`;
   * both pass. Today's writer always emits both, so this is a guard against the
   * contract, not a description of current data.
   */
  diff: Record<string, { before?: unknown; after?: unknown }>;
  /** Raw feed record (object) or raw page excerpt (string). */
  rawSnapshot: string | Record<string, unknown>;
  confidence: number;
  extractionMethod: ExtractionMethod;
  /**
   * The `scrapeRuns` document this proposal came out of.
   *
   * Optional, and permanently so. Every review written before the scraper
   * started stamping it has none, and there is no backfill — the run documents
   * a 2026 proposal belongs to are not reconstructible after the fact. Read
   * paths must render without it rather than treat its absence as an error.
   *
   * Scraper-written and unvalidated, like `existingMeetingId`: guard it with
   * `isPlainMeetingId` before it enters a `doc()` path.
   */
  runId?: string;
  /** Field names the existing meeting has a value for that the source does not assert; approving the review never touches them. */
  sourceOmitted?: string[];
  /** True when every change in `diff` adds a value where none existed before. */
  enrichmentOnly?: boolean;
  /** Unrecognised source format codes carried by the scraper (#118). */
  tagCandidates?: string[];
}

export interface CompletedCoverageSource {
  source: string;
  runId: string;
  status: 'completed';
  sourceAttachmentEnabled: true;
}

export interface OrphanCoverageProvenance {
  scopeId: string;
  coverageRunId: string;
  coveredSources: string[];
  sourceRuns: CompletedCoverageSource[];
}

export interface OrphanScrapeReview extends ScrapeReviewResolution {
  kind: 'orphan';
  existingMeetingId: string;
  observedMeeting: {
    id: string;
    active: true;
    sources: string[];
    [field: string]: unknown;
  };
  proposed: { active: false };
  diff: { active: { before: true; after: false } };
  coverage: OrphanCoverageProvenance;
}

export type ReviewMeetingFormat =
  | 'open'
  | 'closed'
  | 'speaker'
  | 'discussion'
  | 'big-book'
  | 'step-study'
  | 'literature'
  | 'mens'
  | 'womens'
  | 'young-people'
  | 'lgbtq'
  | 'online'
  | 'hybrid'
  | 'wheelchair-accessible'
  | 'child-friendly';

/**
 * A reviewer-grown vocabulary entry (#94). Document id is `slug`.
 *
 * `aliases` carries every raw source format code that has been accepted onto
 * this tag, including the one that created it, so future scrapes resolve them
 * without a reviewer having to re-accept the same code twice.
 */
export interface MeetingFormatTag {
  slug: string;
  label: string;
  aliases: string[];
  active: boolean;
  createdBy: string;
  createdAt: Timestamp;
}

export interface MovedMeetingProposal {
  geohash: string;
  coords: [number, number];
  address: string;
  day: Day;
  name: string;
  note: string;
  time: string;
  type: keyof typeof MeetingType;
  sources: string[];
  formats: ReviewMeetingFormat[];
  active: true;
}

export type MovedObservedMeeting = MovedMeetingProposal & {
  id: string;
  [field: string]: unknown;
};

export interface MovedCandidateProvenance {
  chapterId: string;
  source: string;
  runId: string;
  extractionMethod: ExtractionMethod;
  rawSnapshot: string | Record<string, unknown>;
}

export type MovedMeetingDiff = Partial<{
  [Field in keyof MovedMeetingProposal]: {
    before: MovedMeetingProposal[Field];
    after: MovedMeetingProposal[Field];
  };
}>;

export interface MovedScrapeReview extends ScrapeReviewResolution {
  kind: 'moved';
  existingMeetingId: string;
  observedMeeting: MovedObservedMeeting;
  proposed: MovedMeetingProposal;
  diff: MovedMeetingDiff;
  confidence: number;
  candidate: MovedCandidateProvenance;
  coverage: OrphanCoverageProvenance;
}

export type ScrapeReview =
  | LegacyScrapeReview
  | OrphanScrapeReview
  | MovedScrapeReview;

/**
 * Per-run audit log. Read-only from this app — rules forbid writes.
 *
 * Like `ScrapeReview`, `id` is NOT a stored field: the scraper strips it before
 * writing. A reader that naively spreads `snapshot.data()` gets `id: undefined`
 * — reconstruct it from the snapshot id.
 */
export interface ScrapeRun {
  id: string;
  chapterId: string;
  contentHash: string;
  /** Tier that produced this run's content; null when every tier came up empty. */
  method: ExtractionMethod | null;
  /** Extraction-ladder audit: every tier attempted, in order. */
  tiers: {
    tier: ExtractionMethod;
    status: TierStatus;
    detail?: string;
  }[];
  /** Present only when the LLM tier ran — tokens are spent either way. */
  llm?: {
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
    apiCalls: number;
  };
  counts: {
    added: number;
    updated: number;
    inactive: number;
  };
  /** Omitted by runs that never reached detection (no-content / unchanged). */
  inactiveDetection?:
    | { status: 'detected' }
    | { status: 'skipped'; reason: InactiveSkipReason };
  /** Parser drops and normalizer flags for this run. Absent on pre-PAS-52 docs. */
  flaggedRecords?: {
    dropped: number;
    flagged: number;
  };
  /** Present only on budgeted runs; absent on every unbudgeted run. */
  runGroupId?: string;
  budget?: {
    maxCostUsd?: number;
    maxApiCalls?: number;
    /** Cumulative across the whole run, not this chapter's own spend. */
    spentUsd: number;
    apiCalls: number;
    exhausted: boolean;
    /** True only where this run actually refused the LLM tier for budget. */
    stoppedForBudget: boolean;
  };
  errors: string[];
  startedAt: Timestamp;
  durationMs: number;
}
